import type { Nullable } from '@orama/orama'

import { ArrowUp, PaperPlaneRight, Plus } from '@phosphor-icons/react'
import { effect } from '@preact/signals-core'
import { useSignal, useSignals } from '@preact/signals-react/runtime'
import clx from 'classnames'
import debounce from 'lodash.debounce'
import { useEffect, useRef, useState } from 'react'
import * as $ from '../../signal/searchbox'
import { StarIcon } from '../Icons/Icons'
import { If } from '../If'
import styles from './Summary.module.css'
import { SummaryMarkdown } from './SummaryMarkdown'

type SummaryProps = {
  className?: string
}

export const Summary = ({ className }: SummaryProps) => {
  useSignals()

  const [newChatQuestion, setNewChatQuestion] = useState<string>('')
  const [isChatting, setIsChatting] = useState<boolean>(false)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const theme = useSignal($.theme).value
  const isSummaryExpanded = useSignal($.isSummaryExpanded).value
  const contentScrollHeight = contentRef.current?.scrollHeight
  const summaryHistory = useSignal($.summaryHistory).value
  const isLoadingAnswer = useSignal($.loadingAnswer).value
  const inputRef = useRef<Nullable<HTMLInputElement>>(null)

  const variant = theme.value
  const primaryTheme = variant === 'primary'
  const secondaryTheme = variant === 'secondary'
  const [_, setLast] = useState(0)

  effect(
    debounce(() => {
      setLast((last) => last + 1)
    }, 100)
  )

  const showTitle = primaryTheme || (secondaryTheme && !$.isSummaryExpanded.value)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (contentRef.current) {
      $.isSummaryClamped.value = contentRef.current?.scrollHeight > contentRef.current.clientHeight
    }
  }, [contentScrollHeight, $.isSummaryExpanded.value])

  useEffect(() => {
    if (!$.isSummaryExpanded.value) {
      setIsChatting(false)
    }
  }, [$.isSummaryExpanded.value])

  useEffect(() => {
    if (isChatting) {
      contentRef?.current?.scrollTo({
        left: 0,
        top: contentScrollHeight,
        behavior: 'smooth'
      })
    } else if ($.isSummaryExpanded.value) {
      const userMessages = document.querySelectorAll('[data-role="user-message"]')
      const last = userMessages[userMessages.length - 1]

      try {
        last?.scrollIntoView({
          behavior: 'smooth'
        })
      } catch {}
    } else {
      contentRef?.current?.scrollTo({
        left: 0,
        top: 0
      })
    }
  }, [isChatting, contentScrollHeight, $.isSummaryExpanded.value])

  function handleChatBackSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    e.stopPropagation()

    setIsChatting(true)

    $.generateFollowUpSummary({
      role: 'user',
      content: newChatQuestion
    })

    setNewChatQuestion('')
  }

  if (!isSummaryExpanded.value) {
    return null
  }

  return (
    <div
      className={clx(
        styles.summary__container,
        styles[`summary__container--${variant}`],
        $.isSummaryExpanded.value ? styles.summary__container__expanded : styles.summary__container__clamped,
        className
      )}
    >
      <div
        className={clx(styles.summary__content__container, {
          [styles.summary__expanded]: $.isSummaryExpanded.value,
          [styles['summary__content__container--primary']]: primaryTheme,
          [styles['summary__content__container--secondary']]: secondaryTheme
        })}
      >
        {$.isSummaryExpanded.value && (
          <div
            className={clx(
              styles.summary_chatback_input_container,
              styles[`summary_chatback_input_container--${variant}`]
            )}
          >
            <div className={styles.summary_chatback_input_shadow} />
            <form onSubmit={handleChatBackSubmit}>
              <div
                className={clx(
                  styles.summary_chatback_input__prompt,
                  styles[`summary_chatback_input__prompt--${variant}`]
                )}
              >
                {secondaryTheme && (
                  <span className={styles.summary_title_icon}>
                    <StarIcon />
                  </span>
                )}
                <input
                  type="text"
                  ref={inputRef}
                  value={newChatQuestion}
                  onChange={(e) => setNewChatQuestion(e.target.value)}
                  className={clx(styles.summary_chatback_input, styles[`summary_chatback_input--${variant}`])}
                  placeholder="Ask anything to Orama AI"
                />
                <button type="submit" className={styles.summary_chatback_send_btn}>
                  <PaperPlaneRight size={18} />
                </button>
              </div>
            </form>
          </div>
        )}

        <If condition={!$.summaryHistory.value.length && isLoadingAnswer.value}>
          <div className={styles.summary__content__loading__container}>
            <div className={styles.summary__content__loading} />
          </div>
        </If>

        <If condition={Boolean(!$.summaryHistory.value.length)}>
          <div className={styles.summary__empty__state__container}>
            <If condition={!$.searchTerm.value && !isLoadingAnswer.value}>Ask anything to Orama AI</If>
          </div>
        </If>

        <If condition={Boolean($.summaryHistory.value.length)}>
          <>
            <div
              className={clx(styles.summary__content__wrapper, {
                [styles.summary__content__wrapper__with__padding]: $.isSummaryExpanded.value && primaryTheme
              })}
            >
              <div
                className={clx(styles.summary__content__markdown, {
                  [styles[`summary__content__markdown--${variant}`]]: true,
                  [styles.summary__content__markdown__clamped]: !$.isSummaryExpanded.value,
                  [styles.summary__content__markdown__expanded]: $.isSummaryExpanded.value
                })}
                ref={contentRef}
              >
                {summaryHistory.value.map((item, index) => {
                  if (index === 0 && !$.isSummaryExpanded.value) return null

                  if (!isSummaryExpanded.value) {
                    if (index !== summaryHistory.value.length - 1 || item.role !== 'assistant') return null
                  }

                  return (
                    <div key={`${item.role}-${item.content.length}-${index}`}>
                      <If condition={item.role === 'user'}>
                        <div
                          className={clx(
                            styles.summary__content__user__message,
                            styles[`summary__content__user__message--${variant}`]
                          )}
                          data-role="user-message"
                        >
                          {variant === 'secondary' && (
                            <StarIcon className={styles.summary__content__user__message__icon} />
                          )}
                          <p>{item.content}</p>
                        </div>
                      </If>

                      <If condition={item.role === 'assistant'}>
                        <SummaryMarkdown item={item} />
                      </If>
                    </div>
                  )
                })}
                <If condition={isLoadingAnswer.value}>
                  <div className={styles.summary__content__loading__container}>
                    <div className={styles.summary__content__loading} />
                  </div>
                </If>
              </div>

              <If condition={$.isSummaryClamped.value && !$.isSummaryExpanded.value}>
                <div className={styles.summary__button__container}>
                  <button
                    type="button"
                    className={clx(styles.summary__button__more, styles[`summary__button__more--${variant}`])}
                    onClick={() => {
                      $.isSummaryExpanded.value = true
                    }}
                  >
                    <If condition={primaryTheme}>
                      Continue reading <ArrowUp size={14} style={{ marginLeft: 4 }} />
                    </If>

                    <If condition={secondaryTheme}>Read more</If>

                    {/* show Ask follow up button if content is less than 3 lines */}
                  </button>
                </div>
              </If>

              {!$.isSummaryClamped.value && !$.isSummaryExpanded.value && (
                <button
                  className={clx(styles.summary__button__ask, styles[`summary__button__ask--${variant}`])}
                  onClick={() => {
                    $.isSummaryExpanded.value = true
                  }}
                  type="button"
                >
                  <Plus size="16" />
                  Ask follow up
                </button>
              )}
            </div>
          </>
        </If>
      </div>
    </div>
  )
}
